import { Node, mergeAttributes } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import { EditorContent, useEditor } from "@tiptap/react";
import { Button, message } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../config/serverApiConfig";
import "./instruction-editor.css";

const InstructionVideo = Node.create({
  name: "instructionVideo",
  group: "block",
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "iframe[data-instruction-video]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "iframe",
      mergeAttributes(HTMLAttributes, {
        "data-instruction-video": "true",
        frameborder: "0",
        allowfullscreen: "true",
      }),
    ];
  },
});

function toEditorHtml(value) {
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return value;
  return value
    .split(/\n+/)
    .map((line) => `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") || "<br>"}</p>`)
    .join("");
}

function InstructionEditor({ value, onChange, dir = "rtl", placeholder }) {
  const imageRef = useRef(null);
  const videoRef = useRef(null);
  const [, setToolbar] = useState(0);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      InstructionVideo,
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: toEditorHtml(value),
    editorProps: {
      attributes: {
        dir,
        class: "instruction-editor",
      },
    },
    onUpdate: ({ editor: current }) => onChange?.(current.getHTML()),
    onSelectionUpdate: () => setToolbar((tick) => tick + 1),
    onTransaction: () => setToolbar((tick) => tick + 1),
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const next = toEditorHtml(value);
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  const upload = async (file, kind) => {
    const body = new FormData();
    body.append("file", file);
    const { data } = await axios.post(`${API_BASE_URL}instructions/text/media`, body);
    if (kind === "video") {
      editor.chain().focus().insertContent({ type: "instructionVideo", attrs: { src: data.url } }).run();
    } else {
      editor.chain().focus().setImage({ src: data.url }).run();
    }
  };

  const pick = async (event, kind) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !editor) return;
    const hide = message.loading(kind === "video" ? "ڤیدیۆ بار دەکرێت" : "وێنە بار دەکرێت", 0);
    try {
      await upload(file, kind);
    } catch (err) {
      message.error(kind === "video" ? "ڤیدیۆ بار نەکرا" : "وێنە بار نەکرا");
    } finally {
      hide();
    }
  };

  if (!editor) return null;

  return (
    <div className="instruction-editor-wrap" dir={dir}>
      <div className="instruction-editor-bar">
        <Button htmlType="button" className={editor.isActive("bold") ? "is-on" : ""} onClick={() => editor.chain().focus().toggleBold().run()}>B</Button>
        <Button htmlType="button" className={editor.isActive("italic") ? "is-on" : ""} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></Button>
        <Button htmlType="button" className={editor.isActive("bulletList") ? "is-on" : ""} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</Button>
        <Button htmlType="button" className={editor.isActive("orderedList") ? "is-on" : ""} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</Button>
        <Button htmlType="button" onClick={() => imageRef.current?.click()}>وێنە</Button>
        <Button htmlType="button" onClick={() => videoRef.current?.click()}>ڤیدیۆ</Button>
      </div>
      <input ref={imageRef} type="file" accept="image/*" hidden onChange={(event) => pick(event, "image")} />
      <input ref={videoRef} type="file" accept="video/*" hidden onChange={(event) => pick(event, "video")} />
      <EditorContent editor={editor} />
    </div>
  );
}

export default InstructionEditor;
